
import articleEditService from '@/services/article/ArticleEditService';
import store from '@/store';
import { Component } from 'vue-property-decorator';
import VueApp from '@/@types/app/VueApp';
import ProgressBar from './ProgressBar/ProgressBar.vue';
import extensionService from '@/services/ExtensionService';
import { PostMessage } from '@/other/PostMessage';
import { ExtensionMessageType } from '@/@types/enums/ExtensionMessageType';
import { ArticleVideoEditorState } from '@/components/article-editor/types/ArticleVideoEditorState';
import Loader from '@/components/Loader/Loader.vue';
import { wait } from '@/other/wait';
import { isTrustedIframeMessage } from '@/other/trustedOrigin';

@Component({
  components: {
    Loader,
    ProgressBar,
  },
})
export default class UploadRecordStatus extends VueApp {
  uploadingProgress = 0;
  extensionErrorText = '';
  isInjectApiIframe = false;
  isRunBeforeInjectingApiIframe = false;
  retryAttempts = 0; // State property to track retry attempts

  get articleId() {
    return this.$store.state.articleEditor.article.id;
  }

  get draft() {
    return this.$store.state.articleEditor.article.draft;
  }
  get acceptSessionId() {
    return this.$store.state.articleEditor.article.acceptSessionId;
  }

  get extensionBaseUrl() {
    return this.$store.state.extension.baseUrl;
  }

  onExtensionMessageFromIframe(event: MessageEvent) {
    if (!event.data) return;
    if (!isTrustedIframeMessage(event, this.extensionBaseUrl)) return;

    const postMessage = PostMessage.parse(event.data);
    if (postMessage.environment !== process.env.VUE_APP_APP_ENV) return;

    if (postMessage.isSourceApi() && postMessage.payload.articleId === this.articleId) {
      if (!this.isInjectApiIframe) {
        this.isRunBeforeInjectingApiIframe = true;
      }
      switch (postMessage.message) {
        case ExtensionMessageType.ARTICLE_RECORDING_UPLOAD_FAILED:
          this.extensionErrorText = postMessage.payload.error || 'The upload failed. Please try again.';
          this.uploadingProgress = 0;

          if (this.retryAttempts >= 1) {
            store.state.articleEditor.article.acceptSessionId = undefined;
            store.state.articleEditor.article.isNotUploadedVideo = false;
            articleEditService.saveAsDraft(false);
          }
          break;
        case ExtensionMessageType.ARTICLE_RECORDING_UPLOAD_PROGRESS:
          this.extensionErrorText = ''; // Clear any previous errors on progress
          this.uploadingProgress = postMessage.payload.progress;
          this.$store.commit('setArticleEditorIsVideoProcessing', true);
          break;
        case ExtensionMessageType.ARTICLE_RECORDING_UPLOAD_FINISHED:
          this.uploadingProgress = 100;
          this.$router.go(0); // Use router.go(0) for a full page refresh
          break;
      }
    }
  }

  onExtensionMessage(postMessage: PostMessage) {
    switch (postMessage.message) {
      case ExtensionMessageType.RECORDING_UPLOAD_PROGRESS:
        this.extensionErrorText = ''; // Clear any previous errors on progress
        this.uploadingProgress = postMessage.payload.progress;
        this.$store.commit('setArticleEditorIsVideoProcessing', true);
        break;
      case ExtensionMessageType.RECORDING_UPLOAD_FINISHED:
        this.uploadingProgress = 100;
        this.$router.go(0); // Use router.go(0) for a full page refresh
        break;
    }
  }

  retryUploadRecording() {
    this.retryAttempts++; // Increment the counter on each click
    this.extensionErrorText = '';
    this.isInjectApiIframe = false;

    this.$nextTick(() => {
      this.isInjectApiIframe = true;
    });
  }

  openExtensionRecordings() {
    extensionService.openRecordingsPage();
  }

  handleOffline() {
    this.uploadingProgress = 0;
    this.extensionErrorText = 'Upload failed. Please check your internet connection.';
    // Set attempts high to show the fallback UI immediately for network issues
    this.retryAttempts = 1;
  }

  async created() {
    const editorState = this.$store.state.articleEditor.state;
    if (editorState !== ArticleVideoEditorState.inserting) {
      window.addEventListener('message', this.onExtensionMessageFromIframe);
      await wait(500);
      this.isInjectApiIframe = true;
    } else if (editorState === ArticleVideoEditorState.inserting) {
      //check if draft exist
      if (!this.draft) {
        await articleEditService.saveAsDraft();
      }
      // listening message from extension by regular channel
      extensionService.addMessageListener(this.onExtensionMessage);
    }

    window.addEventListener('offline', this.handleOffline);
  }

  beforeDestroy() {
    window.removeEventListener('message', this.onExtensionMessageFromIframe);
    window.removeEventListener('offline', this.handleOffline);
    extensionService.removeMessageListener(this.onExtensionMessage);
  }
}
